import SoftBadge from "@/components/atoms/SoftBadge";
import Company from "@/interfaces/Company";
import { FormatOptionLabelMeta } from "react-select";

/** « 41234567800012 » → « 412 345 678 00012 », la forme qu'on lit sur un Kbis. */
export const formatSiret = (siret: string) => {
  const digits = siret.replace(/\s/g, "");
  if (!/^\d{14}$/.test(digits)) return siret;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
};

/** Minuscules sans accents, à longueur égale : « Hôtel » et « hotel » se
 *  comparent, et les positions trouvées valent dans le texte d'origine. */
const fold = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/** Le nom, avec la partie qui correspond à la saisie surlignée. */
const Highlight = ({ text, query }: { text: string; query: string }) => {
  const needle = fold(query.trim());
  const folded = fold(text);
  const start = needle && folded.length === text.length ? folded.indexOf(needle) : -1;
  if (start < 0) return <>{text}</>;
  const end = start + needle.length;
  return (
    <>
      {text.slice(0, start)}
      <mark className="p-0 bg-warning-subtle text-reset">
        {text.slice(start, end)}
      </mark>
      {text.slice(end)}
    </>
  );
};

/**
 * Option du sélecteur d'entreprise de la modale de rendez-vous (spec
 * 2026-10-08) : la recherche porte sur tous les champs usuels, alors l'option
 * montre de quoi reconnaître la bonne fiche sans l'ouvrir — SIRET, lieu,
 * téléphone, commercial et télépro, statut à ses couleurs. Une fois choisie,
 * seuls le nom et la ville restent dans le champ.
 */
const CompanyOption = (
  company: Company,
  { context, inputValue }: FormatOptionLabelMeta<Company>,
) => {
  if (context === "value") {
    return (
      <span>
        {company.name}
        {company.city && <span className="text-600"> · {company.city}</span>}
      </span>
    );
  }

  const place = [company.zipCode, company.city].filter(Boolean).join(" ");
  const details = [
    company.siret && `SIRET ${formatSiret(company.siret)}`,
    place,
    company.phone || company.fixPhone,
  ].filter(Boolean);
  const staff = [
    company.ae?.user && `Commercial : ${company.ae.user.fullname}`,
    company.sdr?.user && `Télépro : ${company.sdr.user.fullname}`,
  ].filter(Boolean);
  const status = company.status;

  return (
    <div className="d-flex align-items-start justify-content-between gap-3">
      <div style={{ minWidth: 0 }}>
        <div className="fw-semi-bold text-truncate">
          <Highlight text={company.name} query={inputValue} />
        </div>
        {details.length > 0 && (
          <div className="fs--1 text-600 text-truncate">{details.join(" · ")}</div>
        )}
        {staff.length > 0 && (
          <div className="fs--2 text-500 text-truncate">{staff.join(" · ")}</div>
        )}
      </div>
      {status && (
        <SoftBadge
          bg={status.backgroundColor}
          textColor={status.textColor}
          className="flex-shrink-0"
        >
          {status.name}
        </SoftBadge>
      )}
    </div>
  );
};

export default CompanyOption;
